import { useCallback, useEffect, useMemo, useState } from "react";
import { Edit3, Star, Trash2 } from "lucide-react";
import { api } from "../../services/api";
import { InsightCard, PaginationControls, StatusPill, WorkflowToolbar } from "./AdminPanelParts";
import { useAdminResource } from "./useAdminResource";
import { usePagination } from "./usePagination";
import { ToastStack, useToast } from "./useToast";

const inputClass = "w-full rounded-xl border border-gray-200 bg-gray-50 px-4 py-3 text-sm font-semibold outline-none focus:border-[#4391d1]";

function statusTone(status) {
  return status === "published" ? "good" : "warn";
}

function customerName(review) {
  return review.user?.name || review.user?.email || "Customer";
}

function orderLabel(review) {
  return review.order?.orderNumber || "Order";
}

function Stars({ rating }) {
  return (
    <div className="flex items-center gap-0.5 text-amber-500">
      {[1, 2, 3, 4, 5].map((star) => (
        <Star className="h-4 w-4" fill={Number(rating || 0) >= star ? "currentColor" : "none"} key={star} />
      ))}
    </div>
  );
}

export default function Reviews() {
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("all");
  const [rating, setRating] = useState("all");
  const [editing, setEditing] = useState(null);
  const [editForm, setEditForm] = useState({ rating: 5, comment: "", status: "published" });
  const { dismiss, toast, toasts } = useToast();

  const loadReviews = useCallback(async () => {
    const params = new URLSearchParams();
    if (search.trim()) params.set("search", search.trim());
    if (status !== "all") params.set("status", status);
    if (rating !== "all") params.set("rating", rating);
    const data = await api.get(`/reviews?${params.toString()}`);
    return data.reviews || [];
  }, [rating, search, status]);

  const { data: reviews, error, loading, reload, setData: setReviews } = useAdminResource(loadReviews, []);

  useEffect(() => {
    if (error && !loading) toast(`Could not load reviews: ${error}`, "error");
  }, [error, loading, toast]);

  const metrics = useMemo(() => ({
    total: reviews.length,
    published: reviews.filter((review) => review.status === "published").length,
    hidden: reviews.filter((review) => review.status === "hidden").length,
    average: reviews.length ? (reviews.reduce((sum, review) => sum + Number(review.rating || 0), 0) / reviews.length).toFixed(1) : "0.0",
  }), [reviews]);

  const pagination = usePagination(reviews, [search, status, rating], 10);

  const startEdit = (review) => {
    setEditing(review);
    setEditForm({
      rating: Number(review.rating || 5),
      comment: review.comment || "",
      status: review.status || "published",
    });
  };

  const updateEditForm = (event) => {
    const { name, value } = event.target;
    setEditForm((current) => ({ ...current, [name]: value }));
  };

  const saveReview = async (event) => {
    event.preventDefault();
    if (!editing?._id) return;
    try {
      const data = await api.patch(`/reviews/${editing._id}`, {
        ...editForm,
        rating: Number(editForm.rating),
      });
      setReviews((current) => current.map((review) => review._id === data.review._id ? data.review : review));
      setEditing(null);
      toast("Review updated.", "success");
    } catch (err) {
      toast(err.message, "error");
    }
  };

  const toggleStatus = async (review) => {
    try {
      const data = await api.patch(`/reviews/${review._id}`, {
        status: review.status === "published" ? "hidden" : "published",
      });
      setReviews((current) => current.map((item) => item._id === data.review._id ? data.review : item));
      toast(data.review.status === "published" ? "Review published." : "Review hidden.", "success");
    } catch (err) {
      toast(err.message, "error");
    }
  };

  const deleteReview = async (review) => {
    if (!window.confirm("Delete this product review?")) return;
    try {
      await api.delete(`/reviews/${review._id}`);
      setReviews((current) => current.filter((item) => item._id !== review._id));
      if (editing?._id === review._id) setEditing(null);
      toast("Review deleted.", "success");
    } catch (err) {
      toast(err.message, "error");
    }
  };

  return (
    <div className="p-6 md:p-10">
      <ToastStack dismiss={dismiss} toasts={toasts} />

      <div className="mb-8 flex flex-col gap-4 xl:flex-row xl:items-end xl:justify-between">
        <div>
          <p className="mb-2 text-xs font-bold uppercase tracking-widest text-[#4391d1]">Customer feedback</p>
          <h1 className="text-3xl font-black text-gray-900">Product Reviews</h1>
          <p className="mt-2 max-w-3xl text-sm leading-6 text-gray-500">
            Review verified customer feedback, edit copy when needed, hide inappropriate reviews, or remove spam.
          </p>
        </div>
        <WorkflowToolbar actions={[{ label: "Refresh", primary: true, onClick: reload }]} />
      </div>

      <div className="mb-6 grid gap-5 lg:grid-cols-4">
        <InsightCard title="Reviews" value={metrics.total} note="Matching current filters" />
        <InsightCard title="Published" value={metrics.published} note="Visible on product pages" tone="good" />
        <InsightCard title="Hidden" value={metrics.hidden} note="Moderated or internal only" tone="warn" />
        <InsightCard title="Average" value={metrics.average} note="Average star rating" tone="info" />
      </div>

      <section className="mb-6 rounded-2xl border border-gray-100 bg-white p-5 shadow-sm">
        <div className="grid gap-3 lg:grid-cols-[1fr_180px_180px]">
          <input className={inputClass} onChange={(event) => setSearch(event.target.value)} placeholder="Search product, SKU, customer comment..." value={search} />
          <select className={inputClass} onChange={(event) => setStatus(event.target.value)} value={status}>
            <option value="all">All statuses</option>
            <option value="published">Published</option>
            <option value="hidden">Hidden</option>
          </select>
          <select className={inputClass} onChange={(event) => setRating(event.target.value)} value={rating}>
            <option value="all">All ratings</option>
            {[5, 4, 3, 2, 1].map((item) => <option key={item} value={item}>{item} stars</option>)}
          </select>
        </div>
      </section>

      <div className="grid gap-6">
        <section className="overflow-hidden rounded-2xl border border-gray-100 bg-white shadow-sm">
          <div className="flex items-center justify-between border-b border-gray-100 px-6 py-5">
            <h2 className="text-lg font-black text-gray-900">Review Queue</h2>
            <span className="text-xs font-bold text-gray-400">{loading ? "Loading..." : `${reviews.length} visible`}</span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-left">
              <thead className="bg-gray-50 text-xs uppercase tracking-wide text-gray-400">
                <tr>{["Product", "Customer", "Rating", "Comment", "Status", "Actions"].map((column) => <th className="whitespace-nowrap px-6 py-4 font-black" key={column}>{column}</th>)}</tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {loading ? (
                  <tr><td className="px-6 py-8 text-center text-sm font-bold text-gray-400" colSpan={6}>Loading reviews...</td></tr>
                ) : pagination.paginatedItems.length ? pagination.paginatedItems.map((review) => (
                  <tr className="text-sm text-gray-600" key={review._id}>
                    <td className="min-w-64 px-6 py-4">
                      <div className="font-black text-gray-900">{review.productName}</div>
                      <div className="mt-1 text-xs font-bold text-gray-400">SKU {review.sku || review.productId}</div>
                      <div className="mt-1 text-xs font-bold text-gray-400">{orderLabel(review)}</div>
                    </td>
                    <td className="min-w-48 px-6 py-4">
                      <div className="font-bold text-gray-900">{customerName(review)}</div>
                      <div className="text-xs text-gray-400">{review.user?.email}</div>
                    </td>
                    <td className="px-6 py-4"><Stars rating={review.rating} /></td>
                    <td className="min-w-80 px-6 py-4"><p className="line-clamp-3 font-semibold leading-6">{review.comment}</p></td>
                    <td className="px-6 py-4"><StatusPill tone={statusTone(review.status)}>{review.status}</StatusPill></td>
                    <td className="px-6 py-4">
                      <div className="flex flex-wrap gap-2">
                        <button className="inline-flex items-center gap-1 rounded-lg bg-[#4391d1] px-3 py-2 text-xs font-black text-white" onClick={() => startEdit(review)} type="button"><Edit3 className="h-3.5 w-3.5" /> Edit</button>
                        <button className="rounded-lg border border-gray-200 px-3 py-2 text-xs font-black text-gray-700" onClick={() => toggleStatus(review)} type="button">{review.status === "published" ? "Hide" : "Publish"}</button>
                        <button className="rounded-lg bg-red-50 px-3 py-2 text-xs font-black text-red-600" onClick={() => deleteReview(review)} type="button"><Trash2 className="h-3.5 w-3.5" /></button>
                      </div>
                    </td>
                  </tr>
                )) : (
                  <tr><td className="px-6 py-8 text-center text-sm font-bold text-gray-400" colSpan={6}>No reviews found.</td></tr>
                )}
              </tbody>
            </table>
          </div>
          <PaginationControls pagination={pagination} />
        </section>
      </div>

      {editing && (
        <div className="fixed inset-0 z-[2100] flex items-center justify-center bg-gray-950/50 p-4">
          <button className="absolute inset-0 cursor-default" onClick={() => setEditing(null)} type="button" aria-label="Close edit review popup" />
          <form className="relative w-full max-w-2xl rounded-3xl bg-white p-6 shadow-2xl" onSubmit={saveReview}>
            <div className="mb-5 flex items-start justify-between gap-4">
              <div>
                <p className="text-xs font-black uppercase tracking-widest text-[#4391d1]">Review moderation</p>
                <h2 className="mt-2 text-2xl font-black text-gray-900">Edit Review</h2>
              </div>
              <button className="rounded-full border border-gray-200 px-3 py-1.5 text-sm font-black text-gray-500 hover:text-gray-900" onClick={() => setEditing(null)} type="button">
                Close
              </button>
            </div>

            <div className="mb-5 rounded-xl bg-gray-50 p-4">
              <p className="font-black text-gray-900">{editing.productName}</p>
              <p className="mt-1 text-xs font-bold uppercase text-gray-400">{customerName(editing)} / {orderLabel(editing)}</p>
            </div>

            <div className="grid gap-4 md:grid-cols-2">
              <label className="block">
                <span className="mb-2 block text-xs font-black uppercase text-gray-500">Rating</span>
                <select className={inputClass} name="rating" onChange={updateEditForm} value={editForm.rating}>
                  {[5, 4, 3, 2, 1].map((item) => <option key={item} value={item}>{item} stars</option>)}
                </select>
              </label>
              <label className="block">
                <span className="mb-2 block text-xs font-black uppercase text-gray-500">Status</span>
                <select className={inputClass} name="status" onChange={updateEditForm} value={editForm.status}>
                  <option value="published">Published</option>
                  <option value="hidden">Hidden</option>
                </select>
              </label>
            </div>

            <label className="mt-4 block">
              <span className="mb-2 block text-xs font-black uppercase text-gray-500">Comment</span>
              <textarea className={`${inputClass} min-h-[190px]`} name="comment" onChange={updateEditForm} required value={editForm.comment} />
            </label>

            <div className="mt-5 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
              <button className="rounded-xl border border-gray-200 px-5 py-3 text-sm font-black text-gray-700" onClick={() => setEditing(null)} type="button">Cancel</button>
              <button className="rounded-xl bg-[#4391d1] px-5 py-3 text-sm font-black text-white hover:bg-blue-600" type="submit">Save Review</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
